/**
 * Pixel Tracking Utilities for create-book-pro
 *
 * Simple, decoupled tracking functions that automatically pull data from useBookProStore.
 * No React components needed - just call these functions at the right moments.
 */

import { getBookProStore } from '@/store/useBookProStore';

/**
 * Helper function to check if tracking is enabled
 */
const isTrackingEnabled = () => {
  return process.env.NEXT_PUBLIC_ENABLE_TRACKING === 'true';
};

/**
 * Core tracking function - wraps fbq with safety checks
 */
const trackEvent = (eventName, parameters = {}) => {
  if (!isTrackingEnabled() || typeof window === 'undefined' || !window.fbq) {
    return;
  }

  window.fbq('track', eventName, parameters);
  console.log(`📊 Tracked: ${eventName}`, parameters);
};

/**
 * Extract locale from URL path
 */
const getLocaleFromPath = () => {
  if (typeof window === 'undefined') return 'en-us';
  const path = window.location.pathname;
  const match = path.match(/^\/(en-us|en-uk|en-gb|es-us|es-es)/);
  return match ? match[1] : 'en-us';
};

/**
 * Get email from store if available
 */
const getEmailFromStore = () => {
  const locale = getLocaleFromPath();
  const store = getBookProStore(locale);
  const email = store.getState().formData?.email;
  return email && email.trim() !== '' ? email.toLowerCase().trim() : null;
};

/**
 * Track CustomizeProduct event (fires on page entry)
 * Includes deduplication via localStorage
 */
export const trackCustomizeProduct = () => {
  const storageKey = 'tracked_customize_product_pro';
  const isTracked = typeof window !== 'undefined' && localStorage.getItem(storageKey) === 'true';

  if (isTracked) {
    console.log('📊 CustomizeProduct already tracked (deduplicated)');
    return;
  }

  const eventData = {};
  const email = getEmailFromStore();

  if (email) {
    eventData.em = email;
  }

  trackEvent('CustomizeProduct', eventData);

  if (typeof window !== 'undefined') {
    localStorage.setItem(storageKey, 'true');
  }
};

/**
 * Track CompleteRegistration event (fires when email provided)
 * Includes deduplication via localStorage
 */
export const trackCompleteRegistration = () => {
  const storageKey = 'tracked_complete_registration_pro';
  const isTracked = typeof window !== 'undefined' && localStorage.getItem(storageKey) === 'true';

  if (isTracked) {
    console.log('📊 CompleteRegistration already tracked (deduplicated)');
    return;
  }

  const eventData = {};
  const email = getEmailFromStore();

  if (email) {
    eventData.em = email;
  }

  trackEvent('CompleteRegistration', eventData);

  if (typeof window !== 'undefined') {
    localStorage.setItem(storageKey, 'true');
  }
};

/**
 * Track InitiateCheckout event (fires on checkout screen)
 * Note: No deduplication - user can return to checkout multiple times
 *
 * @param {number} value - Order value
 * @param {string} currency - Currency code (default: 'USD')
 * @param {Array} items - Line items (optional)
 */
export const trackInitiateCheckout = ({ value, currency = 'USD', items = [] } = {}) => {
  const eventData = {
    content_type: 'product',
    content_ids: ['personalized_book'],
    num_items: items.length || 1
  };

  if (value) {
    eventData.value = value;
    eventData.currency = currency;
  }

  const email = getEmailFromStore();
  if (email) {
    eventData.em = email;
  }

  trackEvent('InitiateCheckout', eventData);
};
